import api from './api';

/**
 * Serviço de API para gerenciamento de Dicionários
 * Endpoints para CRUD de tabelas de lookup (código + descrição)
 */

// ==================== TIPOS DE EXAME ====================

/**
 * Lista todos os tipos de exame disponíveis (ECO, ERGO, CAROTIDA)
 */
export const getDicionarioTipos = async () => {
  const response = await api.get('/dicionarios/tipos');
  return response.data;
};

// ==================== CATEGORIAS ====================

/**
 * Lista todas as categorias de um tipo de exame
 * @param {string} tipoExame - ex: 'eco', 'ergo', 'scan'
 */
export const getDicionarioCategorias = async tipoExame => {
  const response = await api.get(`/dicionarios/${tipoExame}/categorias`);
  return response.data;
};

// ==================== ITENS ====================

/**
 * Lista todos os itens de uma categoria
 * @param {string} tipoExame - ex: 'eco', 'ergo', 'carotida'
 * @param {string} categoria - ex: 'observacoes', 'arritmias', 'anatomia'
 */
export const getDicionarioItens = async (tipoExame, categoria) => {
  const response = await api.get(`/dicionarios/${tipoExame}/${categoria}`);
  return response.data;
};

/**
 * Retorna o próximo código disponível (MAX + 1)
 * @param {string} tipoExame
 * @param {string} categoria
 */
export const getProximoCodigo = async (tipoExame, categoria) => {
  const response = await api.get(
    `/dicionarios/${tipoExame}/${categoria}/proximo-codigo`
  );
  return response.data.proximoCodigo;
};

// ==================== CRUD ====================

/**
 * Cria um novo item no dicionário
 * @param {string} tipoExame
 * @param {string} categoria
 * @param {object} item - { codigo, descricao }
 */
export const createDicionarioItem = async (tipoExame, categoria, item) => {
  const response = await api.post(
    `/dicionarios/${tipoExame}/${categoria}`,
    item
  );
  return response.data;
};

/**
 * Atualiza um item existente
 * @param {string} tipoExame
 * @param {string} categoria
 * @param {number} codigo
 * @param {object} item - { descricao }
 */
export const updateDicionarioItem = async (
  tipoExame,
  categoria,
  codigo,
  item
) => {
  const response = await api.put(
    `/dicionarios/${tipoExame}/${categoria}/${codigo}`,
    item
  );
  return response.data;
};

/**
 * Exclui um item do dicionário
 * @param {string} tipoExame
 * @param {string} categoria
 * @param {number} codigo
 */
export const deleteDicionarioItem = async (tipoExame, categoria, codigo) => {
  const response = await api.delete(
    `/dicionarios/${tipoExame}/${categoria}/${codigo}`
  );
  return response.data;
};

// ==================== CONFIGURAÇÃO DE CAMPOS ====================

/**
 * Mapeamento de campos por categoria
 * Define qual campo do BD corresponde a 'codigo' e 'descricao' no frontend
 * SINCRONIZADO com DICIONARIO_CONFIG do backend
 */
export const CAMPO_CONFIG = {
  // ECO - todas as categorias do backend
  eco: {
    obs: { campoCodigo: 'CODOBS', campoDescricao: 'DESCRICAO' },
    achadosve: { campoCodigo: 'CODACHAVE', campoDescricao: 'DESCRICAO' },
    avdiast: { campoCodigo: 'CODAVDIAST', campoDescricao: 'DESCRICAO' },
    vd: { campoCodigo: 'CODVD', campoDescricao: 'DESCRICAO' },
    ae: { campoCodigo: 'CODAE', campoDescricao: 'DESCRICAO' },
    ad: { campoCodigo: 'CODAD', campoDescricao: 'DESCRICAO' },
    vm: { campoCodigo: 'CODVM', campoDescricao: 'DESCRICAO' },
    refvm: { campoCodigo: 'CODREFVM', campoDescricao: 'DESCRICAO' },
    refpm: { campoCodigo: 'CODREFPM', campoDescricao: 'DESCRICAO' },
    vao: { campoCodigo: 'CODVAO', campoDescricao: 'DESCRICAO' },
    refvao: { campoCodigo: 'CODREFVAO', campoDescricao: 'DESCRICAO' },
    refpao: { campoCodigo: 'CODREFPAO', campoDescricao: 'DESCRICAO' },
    vt: { campoCodigo: 'CODVTRICU', campoDescricao: 'DESCRICAO' },
    refvt: { campoCodigo: 'CODREFVT', campoDescricao: 'DESCRICAO' },
    refpt: { campoCodigo: 'CODREFPT', campoDescricao: 'DESCRICAO' },
    vp: { campoCodigo: 'CODVPULMO', campoDescricao: 'DESCRICAO' },
    refvp: { campoCodigo: 'CODREFVP', campoDescricao: 'DESCRICAO' },
    refpp: { campoCodigo: 'CODREFPP', campoDescricao: 'DESCRICAO' },
    pericardio: { campoCodigo: 'CODPERICARDIO', campoDescricao: 'DESCRICAO' },
    vcinf: { campoCodigo: 'CODVCINF', campoDescricao: 'DESCRICAO' },
    aorta: { campoCodigo: 'CODAORTA', campoDescricao: 'DESCRICAO' },
    valvo: { campoCodigo: 'CODVALVO', campoDescricao: 'DESCRICAO' },
    protese: { campoCodigo: 'CODPROTESE', campoDescricao: 'DESCRICAO' },
    contraseg: { campoCodigo: 'CODCONTRASEG', campoDescricao: 'DESCRICAO' },
    contracao: { campoCodigo: 'CODCONSEG', campoDescricao: 'DESCRICAO' },
    regiao: { campoCodigo: 'CODREGIAO', campoDescricao: 'DESCRICAO' },
    conclusoes: { campoCodigo: 'CODCONCLU', campoDescricao: 'DESCRICAO' },
    intensidade: { campoCodigo: 'CODINTENSIDADE', campoDescricao: 'DESCRICAO' },
    predomi: { campoCodigo: 'CODPREDOMI', campoDescricao: 'DESCRICAO' },
    descricao: { campoCodigo: 'CDODESCRICAO', campoDescricao: 'DESCRICAO' },
    situs: { campoCodigo: 'CODSITUS', campoDescricao: 'DESCRICAO' },
  },
  // ERGO - todas as categorias do backend
  ergo: {
    indicacoes: { campoCodigo: 'CODINDI', campoDescricao: 'DESCRICAO' },
    queixas: { campoCodigo: 'CODQUEIXA', campoDescricao: 'FRASE' },
    medicamentos: { campoCodigo: 'CODMEDICTO', campoDescricao: 'FRASE' },
    fatoresrisco: { campoCodigo: 'CODFATRISCO', campoDescricao: 'DESCRICAO' },
    atividadefisica: {
      campoCodigo: 'CODATVFISICA',
      campoDescricao: 'DESCRICAO',
    },
    ecgbasal: { campoCodigo: 'CODECGBASAL', campoDescricao: 'FRASE' },
    alteracoesecg: { campoCodigo: 'CODSEGST', campoDescricao: 'DESCRICAO' },
    arritmias: { campoCodigo: 'CODARRITMIA', campoDescricao: 'DESCRICAO' },
    comportamentopa: { campoCodigo: 'CODCOMPA', campoDescricao: 'DESCRICAO' },
    comentarios: { campoCodigo: 'CODCOMENTARIO', campoDescricao: 'DESCRICAO' },
    conclusoes: { campoCodigo: 'CODCONCLU', campoDescricao: 'DESCRICAO' },
    sas: { campoCodigo: 'CODSAS', campoDescricao: 'DESCRICAO' },
  },
  // CAROTIDA - todas as categorias do backend
  carotida: {
    anatomia: { campoCodigo: 'CODANATOMIA', campoDescricao: 'ANATOMIA' },
    intensidade: {
      campoCodigo: 'CODINTENSIDADE',
      campoDescricao: 'INTENSIDADE',
    },
    velocidade: { campoCodigo: 'CODVELOCIDADE', campoDescricao: 'VELOCIDADE' },
  },
};

/**
 * Obtém a configuração de campos para uma categoria
 * @param {string} tipoExame
 * @param {string} categoria
 * @returns {object} { campoCodigo, campoDescricao }
 */
export const getCampoConfig = (tipoExame, categoria) => {
  const tipoConfig = CAMPO_CONFIG[tipoExame?.toLowerCase()];
  if (!tipoConfig) {
    return { campoCodigo: 'CODIGO', campoDescricao: 'DESCRICAO' };
  }

  const categoriaConfig = tipoConfig[categoria?.toLowerCase()];
  if (!categoriaConfig) {
    return { campoCodigo: 'CODIGO', campoDescricao: 'DESCRICAO' };
  }

  return categoriaConfig;
};

/**
 * Mapeia um item do BD para o formato padronizado do frontend
 * @param {object} item - Item vindo do BD
 * @param {string} tipoExame
 * @param {string} categoria
 * @returns {object} { codigo, descricao }
 */
export const mapItemToFrontend = item => {
  // O backend já retorna os dados mapeados como { codigo, descricao }
  // Então apenas retornamos o item diretamente
  return {
    codigo: item.codigo,
    descricao: item.descricao,
    _original: item,
  };
};
/**
 * Mapeia um item do frontend para o formato do BD
 * @param {object} item - Item do frontend { codigo, descricao }
 * @param {string} tipoExame
 * @param {string} categoria
 * @returns {object} Item no formato do BD
 */
export const mapItemToBackend = (item, tipoExame, categoria) => {
  const { campoCodigo, campoDescricao } = getCampoConfig(tipoExame, categoria);
  const codigo = campoCodigo.toLowerCase();
  const descricao = campoDescricao.toLowerCase();
  return {
    [codigo]: item.codigo,
    [descricao]: item.descricao,
  };
};
